import React, { useEffect, useState } from 'react';
import { useToast } from './Toast';
import api from '../services/api';

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

export default function Account({ user, onLogout, setActiveTab, onUserUpdate }) {
  const [stats,        setStats]        = useState(null);
  const [pref,         setPref]         = useState(user.notification_preference || 'email');
  const [saving,       setSaving]       = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleting,     setDeleting]     = useState(false);
  const [pendingPref,  setPendingPref]  = useState(null);
  const [phoneInput,   setPhoneInput]   = useState('');
  const [phoneError,   setPhoneError]   = useState('');
  const [savingPhone,  setSavingPhone]  = useState(false);
  const { toast } = useToast();

  // On mount, fetch the authoritative preference directly from the DB.
  // This is the only reliable source: the JWT has no notification_preference,
  // and the parent's user object may still be the JWT-decoded version if
  // getMe() hasn't resolved yet.
  useEffect(() => {
    api.getMe()
      .then(data => {
        const fresh = data.user.notification_preference || 'email';
        setPref(fresh);
        if (onUserUpdate) onUserUpdate(data.user);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    api.getWatchlist()
      .then(d => {
        const list = d.watchlist || [];
        setStats({
          total:    list.length,
          notified: list.filter(m => m.notified).length,
          onSale:   list.filter(m => m.tickets_on_sale).length,
        });
      })
      .catch(() => {});
  }, []);

  function validateUSPhone(value) {
    const digits = value.replace(/\D/g, '');
    return digits.length === 10 || (digits.length === 11 && digits[0] === '1');
  }

  async function handlePrefChange(newPref) {
    if (saving || savingPhone) return;
    // Phone check MUST come first — even if pref is already 'sms'/'both',
    // we still need to prompt for a phone number if none is on file.
    if ((newPref === 'sms' || newPref === 'both') && !user.phone) {
      setPendingPref(newPref);
      setPhoneInput('');
      setPhoneError('');
      return;
    }
    // Switching to Email dismisses any open phone prompt
    if (pendingPref) {
      setPendingPref(null);
      setPhoneInput('');
      setPhoneError('');
    }
    // No-op if already on this preference with no pending change
    if (newPref === pref) return;
    setSaving(true);
    try {
      await api.updateNotificationPreference(newPref);
      setPref(newPref);
      if (onUserUpdate) onUserUpdate({ ...user, notification_preference: newPref });
      toast('Notification preference updated', 'success');
    } catch (err) {
      toast(err.message || 'Failed to update preference', 'error');
    } finally {
      setSaving(false);
    }
  }

  async function handlePhoneSave() {
    if (!validateUSPhone(phoneInput)) {
      setPhoneError('Please enter a valid US phone number (e.g. 801-555-1234)');
      return;
    }
    setSavingPhone(true);
    try {
      const { phone: savedPhone } = await api.updatePhone(phoneInput);
      await api.updateNotificationPreference(pendingPref);
      const updatedUser = { ...user, phone: savedPhone, notification_preference: pendingPref };
      setPref(pendingPref);
      if (onUserUpdate) onUserUpdate(updatedUser);
      setPendingPref(null);
      setPhoneInput('');
      setPhoneError('');
      toast('Phone number saved and preference updated', 'success');
    } catch (err) {
      setPhoneError(err.message || 'Failed to save phone number');
    } finally {
      setSavingPhone(false);
    }
  }

  function handlePhoneCancel() {
    setPendingPref(null);
    setPhoneInput('');
    setPhoneError('');
  }

  function handleLogout() {
    api.logout();
    onLogout();
    toast('Signed out successfully', 'info');
  }

  async function handleDeleteAccount() {
    if (deleting) return;
    setDeleting(true);
    try {
      await api.deleteAccount();
      api.logout();
      onLogout();
      toast('Your account has been deleted.', 'info');
    } catch (err) {
      toast(err.message || 'Failed to delete account', 'error');
      setDeleting(false);
      setDeleteConfirm(false);
    }
  }

  return (
    <div className="account-page">
      <div className="account-header">
        <h2>Account</h2>
        <p>Manage your profile and alert preferences</p>
      </div>

      {stats && (
        <div className="acct-card">
          <div className="acct-card-title">Alert Activity</div>
          <div className="acct-stats">
            <div className="stat-box">
              <div className="stat-n">{stats.total}</div>
              <div className="stat-l">Watching</div>
            </div>
            <div className="stat-box">
              <div className="stat-n">{stats.notified}</div>
              <div className="stat-l">Alerts Sent</div>
            </div>
            <div className="stat-box">
              <div className="stat-n">{stats.onSale}</div>
              <div className="stat-l">On Sale Now</div>
            </div>
          </div>
        </div>
      )}

      <div className="acct-card">
        <div className="acct-card-title">Profile</div>
        {[
          { label: 'Name',         value: user.name },
          { label: 'Email',        value: user.email },
          { label: 'Phone',        value: user.phone },
          { label: 'Member Since', value: fmtDate(user.created_at) },
        ].map(r => (
          <div className="acct-row" key={r.label}>
            <span className="acct-row-label">{r.label}</span>
            <span className="acct-row-value">{r.value}</span>
          </div>
        ))}
      </div>

      <div className="acct-card">
        <div className="acct-card-title">Notification Preference</div>
        <p style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 12, lineHeight: 1.6 }}>
          How should Cinema Alert notify you when tickets go on sale?
        </p>
        <div style={{ display: 'flex', gap: 8, marginBottom: 4 }}>
          {[
            { value: 'email', label: '📧 Email' },
            { value: 'sms',   label: '📱 Text' },
            { value: 'both',  label: '📧📱 Both' },
          ].map(opt => {
            const active = (pendingPref ?? pref) === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => handlePrefChange(opt.value)}
                disabled={saving || savingPhone}
                style={{
                  flex: 1,
                  padding: '9px 6px',
                  borderRadius: 8,
                  border: active ? '2px solid #7c3aed' : '2px solid rgba(255,255,255,0.1)',
                  background: active ? 'rgba(124,58,237,0.2)' : 'rgba(255,255,255,0.04)',
                  color: active ? '#ffffff' : 'rgba(255,255,255,0.5)',
                  fontSize: 13,
                  fontWeight: active ? 700 : 400,
                  cursor: (saving || savingPhone) ? 'default' : 'pointer',
                  transition: 'all 0.15s',
                }}
              >
                {opt.label}
              </button>
            );
          })}
        </div>

        {pendingPref && (
          <div style={{ marginTop: 12, padding: '12px 14px', background: 'rgba(124,58,237,0.08)', borderRadius: 8, border: '1px solid rgba(124,58,237,0.25)' }}>
            <p style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 8, lineHeight: 1.5 }}>
              Enter your phone number to receive text alerts:
            </p>
            <input
              type="tel"
              placeholder="(801) 555-1234"
              value={phoneInput}
              onChange={e => { setPhoneInput(e.target.value); setPhoneError(''); }}
              style={{
                width: '100%',
                padding: '9px 10px',
                borderRadius: 7,
                border: phoneError ? '1.5px solid #ef4444' : '1.5px solid rgba(255,255,255,0.15)',
                background: 'rgba(255,255,255,0.06)',
                color: '#fff',
                fontSize: 14,
                boxSizing: 'border-box',
                outline: 'none',
              }}
              autoFocus
              disabled={savingPhone}
            />
            {phoneError && (
              <p style={{ fontSize: 12, color: '#ef4444', marginTop: 5 }}>{phoneError}</p>
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={handlePhoneCancel}
                disabled={savingPhone}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={handlePhoneSave}
                disabled={savingPhone || !phoneInput.trim()}
              >
                {savingPhone ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="acct-card">
        <div className="acct-card-title">How Alerts Work</div>
        <p style={{ fontSize: 14, color: 'var(--text-2)', lineHeight: 1.7, marginBottom: 12 }}>
          Cinema Alert monitors <strong style={{ color: 'var(--text)' }}>Megaplex</strong> and{' '}
          <strong style={{ color: 'var(--text)' }}>Cinemark</strong> theaters in Utah every
          30 minutes. The moment tickets go on sale for a movie on your watchlist, you&apos;ll receive{' '}
          {pref === 'email' && <>an email to <strong style={{ color: 'var(--text)' }}>{user.email}</strong></>}
          {pref === 'sms'   && <>an SMS to <strong style={{ color: 'var(--text)' }}>{user.phone}</strong></>}
          {pref === 'both'  && <>an SMS to <strong style={{ color: 'var(--text)' }}>{user.phone}</strong> and an email to <strong style={{ color: 'var(--text)' }}>{user.email}</strong></>}
          {' '}with direct purchase links.
        </p>
        <button
          className="btn btn-primary btn-sm"
          onClick={() => setActiveTab('watchlist')}
        >
          Manage Watchlist
        </button>
      </div>

      <div style={{ textAlign: 'center', marginTop: 8 }}>
        <button className="btn btn-ghost" onClick={handleLogout}>
          Sign Out
        </button>
      </div>

      {/* Danger Zone */}
      <div className="acct-card acct-danger-zone">
        <div className="acct-card-title acct-danger-title">Danger Zone</div>
        <p style={{ fontSize: 13, color: 'var(--text-2)', lineHeight: 1.6, marginBottom: 14 }}>
          Permanently delete your account and all associated data. This cannot be undone.
        </p>

        {!deleteConfirm ? (
          <button
            className="btn btn-danger-outline"
            onClick={() => setDeleteConfirm(true)}
          >
            Delete My Account
          </button>
        ) : (
          <div className="acct-delete-confirm">
            <p className="acct-delete-confirm-text">
              Are you sure? This will permanently delete your account and all watchlist data.
            </p>
            <div className="acct-delete-confirm-actions">
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => setDeleteConfirm(false)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger btn-sm"
                onClick={handleDeleteAccount}
                disabled={deleting}
              >
                {deleting ? 'Deleting…' : 'Yes, Delete My Account'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
